import Form, { FormListFieldData } from 'antd/lib/form';
import * as React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { SettingField } from '../../types';
import { FieldType, FormFieldData } from '../../helpers/formHelpers';
import { useSourcedSettingOptions } from '../../helpers/settingFieldHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { NumericBadge } from './MonitoringConditionsFields';

// One row per checklist item, drawn like the health metric conditions above it
export const buildChecklistConditionsFormField = (
    settingField: SettingField,
    formField: FormFieldData,
): FormFieldData =>
    ({
        label: settingField.label,
        name: formField.name,
        fieldType: FieldType.List,
        initialValue: formField.initialValue ?? [],
        showDivider: true,
        nestedListFields: [
            {
                fieldType: FieldType.Custom,
                style: { margin: 0 },
                renderField: ({ index, listOperations: { remove }, listFieldData }) => (
                    <ChecklistConditionRow
                        settingField={settingField}
                        listName={formField.name}
                        index={index}
                        listFieldData={listFieldData}
                        remove={remove}
                    />
                ),
            },
        ],
        listChildrenWrapper: (children, { add }) => (
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {children}
                <Button icon={<PlusOutlined style={{ color: runwayColors.primary }} />} onClick={() => add()}>
                    Add checklist item condition
                </Button>
            </Space>
        ),
    }) as FormFieldData;

const ChecklistConditionRow: React.FC<{
    settingField: SettingField;
    listName: FormFieldData['name'];
    index: number;
    listFieldData: FormListFieldData;
    remove: (index: number) => void;
}> = ({ settingField, listName, index, listFieldData, remove }) => {
    const options = useSourcedSettingOptions(settingField.optionsSource) ?? [];
    const selectedIds: (string | undefined)[] = Form.useWatch(listName) ?? [];
    const selectedTitle = options.find((option) => option.value === selectedIds[index])?.label;
    return (
        <>
            <Space
                direction="vertical"
                size={14}
                style={{ width: '100%', backgroundColor: antGrey.grey2, borderRadius: 8, padding: '20px 30px' }}
            >
                <div style={{ width: '100%', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <NumericBadge value={index + 1} />
                    <Form.Item
                        {...listFieldData}
                        noStyle
                        rules={[{ required: true, message: 'Please select a checklist item', type: 'string' }]}
                    >
                        <Select placeholder="Select a checklist item" style={{ width: '100%' }}>
                            {options.map((option) => (
                                <Select.Option
                                    value={option.value}
                                    key={option.value}
                                    disabled={option.value !== selectedIds[index] && selectedIds.includes(option.value)}
                                >
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Button
                        icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                        danger
                        type="link"
                        onClick={() => remove(index)}
                    />
                </div>
                {selectedTitle && (
                    <Typography.Text style={{ color: antGrey.grey7, marginLeft: 44 }}>
                        <strong style={{ color: antGrey.grey8 }}>Checklist item &quot;{selectedTitle}&quot;</strong> is{' '}
                        <strong style={{ color: antGrey.grey8 }}>completed</strong>
                    </Typography.Text>
                )}
            </Space>
            {index < selectedIds.length - 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 16 }}>
                    <Typography.Text style={{ color: antGrey.grey6 }}>
                        <strong>AND</strong>
                    </Typography.Text>
                </div>
            )}
        </>
    );
};
